import * as Yup from "yup";

import { ENV } from "../../../../../utils/constants";

export const INITIAL_VALUES = {
  reference: "",
  notes: "",
  items: [],
};

export function createLocalId() {
  if (
    typeof crypto !== "undefined" &&
    typeof crypto.randomUUID === "function"
  ) {
    return crypto.randomUUID();
  }

  return `inventory-item-${Date.now()}-${Math.random()
    .toString(16)
    .slice(2)}`;
}

export function getImageUrl(imagePath) {
  if (!imagePath) {
    return "";
  }

  const value = String(imagePath);

  if (
    value.startsWith("http://") ||
    value.startsWith("https://") ||
    value.startsWith("blob:")
  ) {
    return value;
  }

  return `${ENV.SERVER_HOST}/${value
    .replace(/\\/g, "/")
    .replace(/^\/+/, "")}`;
}

export function getProductImage(product) {
  const images = Array.isArray(product?.images)
    ? product.images
    : [];

  const coverImage =
    images.find((image) => image?.isCover) || images[0];

  return getImageUrl(coverImage?.path);
}

export function getAttributeName(attribute) {
  if (attribute?.schema && typeof attribute.schema === "object") {
    return attribute.schema.name || "";
  }

  return attribute?.name || "";
}

export function getAttributeValue(attribute) {
  if (
    attribute?.classification &&
    typeof attribute.classification === "object"
  ) {
    return (
      attribute.classification.name ||
      attribute.classification.value ||
      attribute.value ||
      ""
    );
  }

  return attribute?.label || attribute?.value || "";
}

export function getVariantLabel(variant) {
  const attributes = Array.isArray(variant?.attributes)
    ? variant.attributes
    : [];

  if (attributes.length === 0) {
    return "Variante única";
  }

  return attributes
    .map((attribute) => {
      const name = getAttributeName(attribute);
      const value = getAttributeValue(attribute);

      if (name && value) {
        return `${name}: ${value}`;
      }

      return value || name;
    })
    .filter(Boolean)
    .join(" · ");
}

export function flattenProducts(products = []) {
  if (!Array.isArray(products)) {
    return [];
  }

  return products.flatMap((product) => {
    const productId = String(product?._id || "");

    if (!productId) {
      return [];
    }

    const variants = Array.isArray(product?.variants)
      ? product.variants
      : [];

    return variants
      .filter(
        (variant) =>
          variant?.enabled !== false &&
          Boolean(variant?._id)
      )
      .map((variant) => ({
        productId,
        productCode: String(product?.id || ""),
        productName: String(product?.name || ""),
        productImage: getProductImage(product),

        variantId: String(variant._id),
        sku: String(variant?.sku || ""),
        variantLabel: getVariantLabel(variant),

        stock: Number(variant?.stock) || 0,

        cost:
          variant?.cost === null ||
          variant?.cost === undefined
            ? ""
            : Number(variant.cost),
      }));
  });
}

export function createEntryItem(existence = {}) {
  const productId = String(
    existence.productId ||
      existence.product?._id ||
      existence.product ||
      ""
  ).trim();

  const variantId = String(
    existence.variantId ||
      existence.variant?._id ||
      existence.variant ||
      ""
  ).trim();

  return {
    localId: createLocalId(),

    product: productId,
    variantId,

    productName: existence.productName || "",
    productCode: existence.productCode || "",
    productImage: existence.productImage || "",
    sku: existence.sku || "",
    variantLabel: existence.variantLabel || "Variante única",

    // Antes no se copiaba: la columna "Disponible" siempre mostraba 0
    // porque el campo nunca se llenaba al agregar la variante.
    availableStock: Number(existence.stock) || 0,

    quantity: 1,

    unitCost:
      existence.cost === "" ||
      existence.cost === null ||
      existence.cost === undefined
        ? ""
        : Number(existence.cost),
  };
}

export function formatCurrency(value) {
  return `Q ${(Number(value) || 0).toLocaleString("es-GT", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

export function formatNumber(value) {
  return (Number(value) || 0).toLocaleString("es-GT");
}

const inventoryEntryItemSchema = Yup.object({
  localId: Yup.string().required(),

  product: Yup.string()
    .trim()
    .required("El producto es obligatorio."),

  variantId: Yup.string()
    .trim()
    .required("La variante es obligatoria."),

  quantity: Yup.number()
    .typeError("La cantidad debe ser un número válido.")
    .integer("La cantidad debe ser un número entero.")
    .min(1, "La cantidad debe ser mayor a 0.")
    .required("La cantidad es obligatoria."),

  unitCost: Yup.number()
    .transform((value, originalValue) =>
      originalValue === "" || originalValue === null
        ? null
        : value
    )
    .nullable()
    .typeError("El costo debe ser un número válido.")
    .min(0, "El costo debe ser mayor o igual a 0."),
});

export const validationSchema = Yup.object({
  reference: Yup.string()
    .trim()
    .max(100, "La referencia no puede superar 100 caracteres."),

  notes: Yup.string()
    .trim()
    .max(1000, "Las notas no pueden superar 1,000 caracteres."),

  items: Yup.array()
    .of(inventoryEntryItemSchema)
    .min(1, "Agrega al menos un producto o variante.")
    .required("Agrega al menos un producto o variante.")
    .test(
      "unique-variants",
      "No puedes agregar la misma variante más de una vez.",
      (items) => {
        if (!Array.isArray(items)) {
          return true;
        }

        const keys = items.map(
          (item) => `${item.product}-${item.variantId}`
        );

        return new Set(keys).size === keys.length;
      }
    ),
});